/**
 * The basket: <cw-basket> is the drawer (a sheet on phones, a 420 panel at 600+), and
 * <cw-basket-content> is what is in it, the same on the /cart page.
 * design-handoff/specs/cart-drawer-spec.md, components/basket-drawer.md (v13).
 *
 * The lines, aisle cards and totals are Liquid (snippets/cw-basket-body.liquid), asked for
 * again after every change with the Section Rendering API, so MRP, aisles and money are always
 * Shopify's. What depends on the address or on judgement is drawn here:
 *
 * - Delivery row, progress card, "You save" and the amount on CHECKOUT, from Flow's answer for
 *   the chosen address (cw-delivery.js). Flow is the delivery engine: it decides the fee, the
 *   free-delivery bar and whether express exists; this file only writes it the design's way
 *   (§6.1). The fee struck out beside "Free" is Flow's `waived`, never a number made up here.
 * - Suggestion rows, from the customer's own order history only (§8.1 reasons 1-3). The
 *   prototype's kitchen pairs are demo rules (§12); nothing else is suggested until real
 *   pairings exist. Max 2 per card, strongest first; 2 dismissals end them for the visit;
 *   a slot is not refilled while the drawer is open (§4.3).
 * - Missing-aisle tiles (§5, v13 recs10): aisles the basket's aisles pair with
 *   (data/basket-reasons.json), core aisles first; "You may be running low" when a product
 *   bought before is due again and its aisle is empty.
 */
import { cart, MAX_PER_LINE } from '@cw/cart';
import { CwSheet } from '@cw/sheet';
import { view as deliveryView } from '@cw/delivery';
import { AISLES, isOpen } from '@cw/aisles';
import { esc, rupees, productData, defaultPack } from '@cw/products';

const read = (id) => {
  try {
    return JSON.parse(document.getElementById(id)?.textContent || 'null');
  } catch {
    return null;
  }
};
const REASONS = read('cw-basket-reasons') || { pairs: {} };
const HISTORY = read('cw-history') || [];
const CORE = ['atta', 'dal', 'rice', 'spices', 'oils'];
const OFF_KEY = 'cw:basket:off';
const BACK_KEY = 'cw:back';
const DAY = 86400;

const load = (key, fallback) => {
  try {
    return JSON.parse(sessionStorage.getItem(key) || 'null') ?? fallback;
  } catch {
    return fallback;
  }
};
const save = (key, value) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // remembered for this page only
  }
};

/** "31 m", "6.8 km" (v13 applyFees). */
function distance(km) {
  const k = Number(km);
  if (!Number.isFinite(k)) return '';
  return k < 1 ? `${Math.round(k * 1000)} m` : `${Math.round(k * 10) / 10} km`;
}

/** Dismissed suggestions this visit: {products: {id: true}, count}. */
const off = () => load(OFF_KEY, { products: {}, count: 0 });

/** Slots used up while the drawer is open (added or dismissed): not refilled (§4.3). */
const holds = {};

/** History, with the reason each product earns (§8.1, strongest first). */
function historyReasons() {
  const now = Date.now() / 1000;
  return HISTORY.map((h) => {
    const since = (now - h.last) / DAY;
    const gap = h.n > 1 ? (h.last - h.first) / DAY / (h.n - 1) : null;
    if (gap && gap >= 7 && since >= gap) return { ...h, low: true, why: `Bought ${Math.round(since)} days ago`, rank: 3 };
    if (h.n > 1) return { ...h, why: 'You usually buy this', rank: 2 };
    return { ...h, why: 'Bought before', rank: 1 };
  });
}

class CwBasketContent extends HTMLElement {
  connectedCallback() {
    this.lines = JSON.parse(this.querySelector('[data-cw="basket-lines"]')?.textContent || '[]');
    this.addEventListener('click', this.#onClick);
    document.addEventListener('cw:delivery:change', this.#paintDelivery);
    this.#paintDelivery();
    this.#paintSuggestions();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener('cw:delivery:change', this.#paintDelivery);
  }

  get total() {
    return Number(this.dataset.total || 0);
  }

  /** §6 footer: delivery row, You save, progress card, CHECKOUT amount. */
  #paintDelivery = () => {
    const foot = this.querySelector('.cw-bk__foot');
    if (!foot) return;
    const $ = (name) => foot.querySelector(`[data-cw="${name}"]`);
    const line = $('dl-line');
    const fee = $('dl-fee');
    const was = $('dl-was');
    const nudge = $('nudge');
    const done = $('done');
    const pay = $('pay');
    const saveRow = $('save');
    const saveAmt = $('save-amt');
    const goods = Number(this.dataset.save || 0);
    const total = this.total;
    const rupeesTotal = total / 100;

    const v = deliveryView();
    const a = v.answer && v.answer.spot !== 'origin' ? v.answer : null;
    let feePaise = 0;
    let waived = 0;
    nudge.hidden = true;
    done.hidden = true;
    was.hidden = true;
    fee.classList.remove('is-free');

    if (!a) {
      // No address yet: the time of day is known, the fee is not (it depends on the distance).
      const when = v.answer?.standard?.when;
      line.textContent = when ? `Standard · ${when}` : 'Delivery';
      fee.innerHTML = '<button type="button" class="cw-bk__setloc" data-open-address>Set location</button>';
    } else if (!a.serviceable) {
      line.textContent = `Pick up at our shop${a.pickup?.when ? ` · ${a.pickup.when}` : ''}`;
      fee.textContent = 'Free';
      fee.classList.add('is-free');
    } else {
      const std = a.standard || {};
      const ex = a.express || null;
      const useEx = !!(ex && ex.free);
      const pick = useEx ? ex : std;
      feePaise = Math.round(Number(pick.price || 0) * 100);
      line.textContent = `${useEx ? 'Express' : 'Standard'} · ${pick.when || ''} · ${distance(a.distance_km)}`;
      if (feePaise > 0) fee.textContent = rupees(feePaise);
      else {
        fee.textContent = 'Free';
        fee.classList.add('is-free');
        waived = Math.round(Number(pick.waived || 0) * 100);
        if (waived > 0) {
          was.hidden = false;
          was.innerHTML = `<span class="cw-sr">was </span>${esc(rupees(waived))}`;
        }
      }

      // Progress (§6.1). One bar when express is off: there is no second goal.
      const bar = Number(a.free_over || 0);
      const exBar = ex ? Number(ex.free_over || 0) : 0;
      const left = Math.max(0, Math.ceil(bar - rupeesTotal));
      const exLeft = ex ? Math.max(0, Math.ceil(exBar - rupeesTotal)) : 0;
      const seg1 = $('seg1');
      const seg2 = $('seg2');
      const label2 = $('seg2-label');
      const fill = (el, f) => el.querySelector('.cw-bk__fill').style.setProperty('width', `${Math.round(Math.max(0, Math.min(1, f)) * 100)}%`);
      if (left > 0 || (ex && exLeft > 0)) {
        let text;
        if (left > 0) text = left < 50 ? `Almost there — ₹${left.toLocaleString('en-IN')} more for free delivery` : `₹${left.toLocaleString('en-IN')} more for free delivery`;
        else text = exLeft <= (exBar - bar) / 2 ? `Free delivery ✓ · ₹${exLeft.toLocaleString('en-IN')} more for free express` : 'Free delivery unlocked ✓';
        $('nudge-text').textContent = text;
        seg1.style.flex = String(Math.max(1, bar));
        fill(seg1, bar ? rupeesTotal / bar : 1);
        seg2.hidden = !ex;
        label2.hidden = !ex;
        if (ex) {
          seg2.style.flex = String(Math.max(1, exBar - bar));
          fill(seg2, (rupeesTotal - bar) / Math.max(1, exBar - bar));
        }
        nudge.hidden = false;
      } else {
        done.textContent = ex ? 'Free express delivery unlocked ✓' : 'Free delivery unlocked ✓';
        done.hidden = false;
      }
    }

    const saved = goods + waived;
    saveRow.hidden = saved <= 0;
    saveAmt.textContent = rupees(saved);
    pay.textContent = rupees(total + feePaise);
  };

  /** Suggestion rows in the cards, and the missing-aisle module (§4-5). */
  #paintSuggestions() {
    const cards = [...this.querySelectorAll('.cw-bk__card[data-aisle]')];
    if (!cards.length) return;
    const inBasket = new Set(this.lines.map((l) => String(l.p)));
    const aisles = [...new Set(this.lines.map((l) => l.a).filter((a) => a && a !== 'other'))];
    const nA = aisles.length;
    const gone = off();
    // Product suggestions wait for the owner's go (theme setting "Suggest products in the basket").
    const suggest = this.dataset.suggest === 'true';
    const capped = gone.count >= 2 || !suggest;

    // History candidates that can be added in one tap.
    const candidates = historyReasons()
      .filter((h) => !inBasket.has(String(h.id)) && !gone.products[h.id])
      .map((h) => {
        const m = productData(h.id);
        const dv = m && defaultPack(m);
        return m && dv && m.aisle ? { ...h, m, dv } : null;
      })
      .filter(Boolean)
      .sort((x, y) => y.rank - x.rank || y.last - x.last);

    let shown = 0;
    for (const card of cards) {
      const box = card.querySelector('[data-cw="recs"]');
      if (!box) continue;
      const id = card.dataset.aisle;
      const room = 2 - (holds[id] || 0);
      const mine = capped ? [] : candidates.filter((c) => c.m.aisle === id).slice(0, Math.max(0, room));
      const rows = [];
      for (const c of mine) {
        if (nA >= 7 && shown >= 1) break;
        rows.push(this.#suggestionRow(c, id));
        shown += 1;
      }
      box.innerHTML = rows.join('');
    }

    // Missing aisles (§5): none when the basket is empty or already covers 7+ aisles.
    const miss = this.querySelector('[data-cw="miss"]');
    if (!miss) return;
    miss.hidden = true;
    if (!nA || nA >= 7) return;
    const has = new Set(aisles);
    const low = suggest ? candidates.find((c) => c.low && !has.has(c.m.aisle)) : null;
    if (low) {
      miss.innerHTML = `<div class="cw-bk__misshead">You may be running low</div>${this.#productTile(low)}`;
      miss.hidden = false;
      return;
    }
    const score = {};
    const why = {};
    for (const from of aisles) {
      const pairs = REASONS.pairs?.[from] || {};
      for (const to of Object.keys(pairs)) {
        if (has.has(to) || !isOpen(to)) continue;
        score[to] = (score[to] || 0) + 1;
        if (pairs[to] && !why[to]) why[to] = pairs[to];
      }
    }
    const order = AISLES.map((x) => x.id);
    let cand = Object.keys(score).sort(
      (x, y) => score[y] - score[x] || (CORE.includes(x) ? 0 : 1) - (CORE.includes(y) ? 0 : 1) || order.indexOf(x) - order.indexOf(y)
    );
    // The aisle the squirrel points at: its next pick while the shopper is still in the aisle
    // it just filled, else its target.
    const shelf = load('cw:shelf', {});
    const target = shelf?.pending || shelf?.target;
    if (target && cand.includes(target)) cand = [target, ...cand.filter((x) => x !== target)];
    const picked = cand.slice(0, nA === 1 ? 1 : 2);
    if (!picked.length) return;
    const allCore = picked.every((x) => CORE.includes(x));
    const monthly = this.dataset.monthly === 'true';
    const head = nA === 1 ? 'You may also need' : !allCore ? 'You may also want' : monthly ? 'Still missing' : 'You may still need';
    const tiles = picked.map((id, i) => ({ aisle: AISLES.find((x) => x.id === id), why: i === 0 ? why[id] || '' : '' })).filter((t) => t.aisle);
    miss.innerHTML = `<div class="cw-bk__misshead">${esc(head)}</div>${
      tiles.length === 1
        ? `<div class="cw-bk__tile">
            <span class="cw-bk__tileicon" style="background-image:url('${esc(tiles[0].aisle.icon)}')"></span>
            <span class="cw-bk__tiletext"><span class="cw-bk__tilename">${esc(tiles[0].aisle.name)}</span>${tiles[0].why ? `<span class="cw-bk__tilewhy">${esc(tiles[0].why)}</span>` : ''}</span>
            <button type="button" class="cw-bk__browse" data-browse="${esc(tiles[0].aisle.id)}">BROWSE</button>
          </div>`
        : `<div class="cw-bk__tiles">${tiles
            .map(
              (t) => `<div class="cw-bk__tile cw-bk__tile--half">
                <span class="cw-bk__tilerow"><span class="cw-bk__tileicon" style="background-image:url('${esc(t.aisle.icon)}')"></span>
                <span class="cw-bk__tiletext"><span class="cw-bk__tilename">${esc(t.aisle.name)}</span><span class="cw-bk__tilewhy">${esc(t.why)}</span></span></span>
                <button type="button" class="cw-bk__browse" data-browse="${esc(t.aisle.id)}">BROWSE</button>
              </div>`
            )
            .join('')}</div>`
    }`;
    miss.hidden = false;
  }

  #suggestionRow(c, card) {
    const cross = c.m.aisle !== card;
    const icon = AISLES.find((x) => x.id === c.m.aisle)?.icon;
    const mrp = c.dv.compare > c.dv.price ? c.dv.compare : 0;
    return `<div class="cw-bk__rec" data-rec="${c.id}" data-variant="${c.dv.id}" data-card="${esc(card)}">
        <span class="cw-bk__recthumb"><span style="background-image:url('${esc(c.dv.image || c.m.image || '')}')"></span>${
          cross && icon ? `<span class="cw-bk__cross" style="background-image:url('${esc(icon)}')"></span>` : ''
        }</span>
        <span class="cw-bk__text"><span class="cw-bk__recname">${esc(c.m.title)}</span><span class="cw-bk__recmeta">${esc(`${c.dv.label} · ${c.why}`)}</span></span>
        <span class="cw-bk__right">
          <span class="cw-bk__amtrow">${mrp ? `<span class="cw-bk__mrp"><span class="cw-sr">MRP </span>${esc(rupees(mrp))}</span>` : ''}<span class="cw-bk__recprice">${esc(rupees(c.dv.price))}</span></span>
          <span class="cw-bk__recact">
            <button type="button" class="cw-bk__skip" data-skip aria-label="Not now">${SKIP_ICON}</button>
            <button type="button" class="cw-bk__recadd" data-add aria-label="Add ${esc(c.m.title)} ${esc(c.dv.label)}">ADD</button>
          </span>
        </span>
      </div>`;
  }

  #productTile(c) {
    const mrp = c.dv.compare > c.dv.price ? c.dv.compare : 0;
    return `<div class="cw-bk__tile cw-bk__tile--product" data-rec="${c.id}" data-variant="${c.dv.id}">
        <span class="cw-bk__recthumb"><span style="background-image:url('${esc(c.dv.image || c.m.image || '')}')"></span></span>
        <span class="cw-bk__text"><span class="cw-bk__name">${esc(c.m.title)}</span><span class="cw-bk__recmeta">${esc(`${c.dv.label} · ${c.why}`)}</span></span>
        <span class="cw-bk__right">
          <span class="cw-bk__amtrow">${mrp ? `<span class="cw-bk__mrp"><span class="cw-sr">MRP </span>${esc(rupees(mrp))}</span>` : ''}<span class="cw-bk__amt">${esc(rupees(c.dv.price))}</span></span>
          <button type="button" class="cw-bk__recadd" data-add aria-label="Add ${esc(c.m.title)} ${esc(c.dv.label)}">ADD</button>
        </span>
      </div>`;
  }

  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    // Light stepper: the screen moves at once; the basket follows (cw-cart.js queues it).
    const step = target.closest('.cw-bk__pm');
    if (step) {
      const row = step.closest('.cw-bk__line');
      if (navigator.onLine === false) return document.dispatchEvent(new CustomEvent('cw:offline'));
      const q = cart.qtyOf(row.dataset.variant) + Number(step.dataset.step);
      if (q > MAX_PER_LINE) return;
      this.#paintLine(row, Math.max(0, q));
      cart.setQty(row.dataset.variant, Math.max(0, q), this, { productId: row.dataset.product });
      return;
    }

    // The custom mix goes out whole (its lines are never stepped); UNDO puts the same mix back,
    // which Flow still has on record.
    const mixOut = target.closest('[data-mix-remove]');
    if (mixOut) {
      if (navigator.onLine === false) return document.dispatchEvent(new CustomEvent('cw:offline'));
      const mix = cart.mix;
      if (!mix) return;
      const lines = mix.lines.map((item) => ({ id: item.variant_id, quantity: item.quantity, properties: item.properties }));
      const grind = mixOut.closest('[data-mix]')?.dataset.grind || '';
      mixOut.disabled = true;
      cart.replaceMix([], { _mix_grind: '' }).catch(() => (mixOut.disabled = false));
      document.dispatchEvent(
        new CustomEvent('cw:toast', {
          detail: { text: 'Your mix is out of the basket', undo: () => cart.replaceMix(lines, { _mix_grind: grind }) },
        })
      );
      return;
    }

    const add = target.closest('[data-add]');
    if (add) {
      const rec = add.closest('[data-rec]');
      if (navigator.onLine === false) return document.dispatchEvent(new CustomEvent('cw:offline'));
      if (rec.dataset.card) holds[rec.dataset.card] = (holds[rec.dataset.card] || 0) + 1;
      add.disabled = true;
      cart.setQty(rec.dataset.variant, cart.qtyOf(rec.dataset.variant) + 1, this, { productId: rec.dataset.rec });
      return;
    }

    const skip = target.closest('[data-skip]');
    if (skip) return this.#dismiss(skip.closest('[data-rec]'));

    const undo = target.closest('[data-undo]');
    if (undo) {
      const gone = off();
      delete gone.products[undo.dataset.undo];
      gone.count = Math.max(0, gone.count - 1);
      save(OFF_KEY, gone);
      const card = undo.dataset.card;
      if (card) holds[card] = Math.max(0, (holds[card] || 0) - 1);
      clearTimeout(this.undoTimer);
      this.#paintSuggestions();
      return;
    }

    const browse = target.closest('[data-browse]');
    if (browse) {
      const aisle = AISLES.find((x) => x.id === browse.dataset.browse);
      if (!aisle) return;
      save(BACK_KEY, { to: 'basket', aisle: aisle.id });
      window.location.href = aisle.url;
      return;
    }

    if (target.closest('[data-open-address]')) {
      document.dispatchEvent(new CustomEvent('cw:address:open', { cancelable: true, detail: { source: target } }));
      return;
    }

    const back = target.closest('[data-cw="back"]');
    if (back && document.referrer.startsWith(window.location.origin) && window.history.length > 1) {
      event.preventDefault();
      window.history.back();
      return;
    }

    if (target.closest('[data-cw="keep"]') && this.closest('cw-basket')) {
      event.preventDefault();
      this.closest('cw-basket').close();
    }
  };

  /** Quantity, amount and MRP of one line, before the basket confirms. 0 folds the line away. */
  #paintLine(row, q) {
    if (q === 0) {
      row.classList.add('is-leaving');
      return;
    }
    row.querySelector('[data-cw="q"]').textContent = String(q);
    row.querySelector('[data-cw="amt"]').textContent = rupees(Number(row.dataset.price) * q);
    const mrp = row.querySelector('[data-cw="mrp"]');
    if (mrp) mrp.lastChild.textContent = rupees(Number(row.dataset.mrp) * q);
    const minus = row.querySelector('.cw-bk__pm[data-step="-1"]');
    const name = row.querySelector('.cw-bk__name')?.textContent || '';
    minus.setAttribute('aria-label', q === 1 ? `Remove ${name}` : `Remove one ${name}`);
  }

  /** "Hidden for today · name" + UNDO for 4 s, then the card closes up (§4.3). */
  #dismiss(rec) {
    const gone = off();
    gone.products[rec.dataset.rec] = true;
    gone.count += 1;
    save(OFF_KEY, gone);
    const card = rec.dataset.card;
    if (card) holds[card] = (holds[card] || 0) + 1;
    const name = rec.querySelector('.cw-bk__recname, .cw-bk__name')?.textContent || '';
    rec.outerHTML = `<div class="cw-bk__gone"><span>Hidden for today · ${esc(name)}</span><button type="button" data-undo="${esc(rec.dataset.rec)}" data-card="${esc(card || '')}">UNDO</button></div>`;
    clearTimeout(this.undoTimer);
    this.undoTimer = setTimeout(() => {
      for (const el of this.querySelectorAll('.cw-bk__gone')) el.remove();
    }, 4000);
  }
}

const SKIP_ICON =
  '<svg width="10" height="10" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M208.49 191.51a12 12 0 0 1-17 17L128 145l-63.51 63.49a12 12 0 0 1-17-17L111 128 47.51 64.49a12 12 0 0 1 17-17L128 111l63.51-63.52a12 12 0 0 1 17 17L145 128Z"/></svg>';

/**
 * Keeps a basket section current: after the basket settles, ask Shopify for the section again
 * and swap it in, keeping the scroll position. Used by the drawer and the /cart page.
 */
class SectionRefresher {
  constructor(sectionEl) {
    this.el = sectionEl;
    this.id = sectionEl.id.replace(/^shopify-section-/, '');
    this.timer = null;
    this.abort = null;
  }

  schedule(delay = 350) {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.run(), delay);
  }

  /** Draw the basket again; true when the fresh one is in. */
  async run() {
    clearTimeout(this.timer);
    // Mid-change the server would draw the basket as it was; the last change schedules again.
    if (cart.busy) return false;
    this.abort?.abort();
    this.abort = new AbortController();
    try {
      const url = `${window.location.pathname}?section_id=${encodeURIComponent(this.id)}`;
      const response = await fetch(url, { signal: this.abort.signal, headers: { Accept: 'text/html' } });
      if (!response.ok) return false;
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.getElementById(this.el.id);
      if (!fresh) return false;
      const scroller = this.el.querySelector('.cw-bk__scroll');
      const top = scroller?.scrollTop || 0;
      const focused = document.activeElement && this.el.contains(document.activeElement) ? document.activeElement : null;
      const focusKey = focused?.closest('.cw-bk__line')?.dataset.key;
      const focusStep = focused?.dataset.step;
      this.el.replaceChildren(...fresh.childNodes);
      const again = this.el.querySelector('.cw-bk__scroll');
      if (again) again.scrollTop = top;
      if (focusKey) this.el.querySelector(`.cw-bk__line[data-key="${CSS.escape(focusKey)}"] .cw-bk__pm[data-step="${focusStep}"]`)?.focus({ preventScroll: true });
      return true;
    } catch (error) {
      if (error.name !== 'AbortError') {
        // The basket itself is right (cw-cart.js); only this view is a moment behind.
      }
      return false;
    }
  }
}

/** The drawer. Opens on 'cw:basket:open' (basket bar VIEW CART, header Basket). */
class CwBasket extends CwSheet {
  #refresher = null;
  #stale = false;
  #behind = 0;

  connectedCallback() {
    super.connectedCallback();
    const section = document.getElementById('shopify-section-cw-basket');
    const slot = this.querySelector('[data-cw="basket-slot"]');
    if (section && slot && !slot.contains(section)) slot.append(section);
    if (section) this.#refresher = new SectionRefresher(section);
    document.addEventListener('cw:basket:open', this.#onOpen);
    document.addEventListener('cw:cart:change', this.#onCart);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('cw:basket:open', this.#onOpen);
    document.removeEventListener('cw:cart:change', this.#onCart);
  }

  #onOpen = async (event) => {
    if (!this.#refresher) return;
    event.preventDefault();
    // On the /cart page the basket is already the page.
    if (document.body.dataset.template === 'cart') return window.scrollTo({ top: 0, behavior: 'smooth' });
    const source = event.detail?.source;
    // A basket that changed while the drawer was shut is drawn before the drawer slides in,
    // not after: new content mid-slide changes the sheet's height, and a bottom sheet then
    // jumps (owner, 1 Oct). Usually the background redraw below has already done it.
    if (this.#stale) {
      clearTimeout(this.#behind);
      const fresh = await Promise.race([this.#refresher.run(), new Promise((r) => setTimeout(() => r(false), 700))]);
      if (fresh === true) this.#stale = false;
    }
    this.open(source instanceof HTMLElement ? source : undefined);
    if (this.#stale) {
      this.#stale = false;
      this.#refresher.run();
    }
  };

  /** Open: follow every change. Closed: redraw quietly in the background, so it opens current. */
  #onCart = () => {
    if (this.isOpen) return this.#refresher?.schedule();
    this.#stale = true;
    clearTimeout(this.#behind);
    this.#behind = setTimeout(async () => {
      if (!this.isOpen && (await this.#refresher?.run())) this.#stale = false;
    }, 600);
  };

  onClosed() {
    for (const key of Object.keys(holds)) delete holds[key];
  }
}

/** The /cart page: the same content, kept current the same way. */
class CwCartPage extends HTMLElement {
  connectedCallback() {
    const section = this.closest('.shopify-section');
    if (!section) return;
    this.refresher = new SectionRefresher(section);
    document.addEventListener('cw:cart:change', this.#onCart);
  }

  disconnectedCallback() {
    document.removeEventListener('cw:cart:change', this.#onCart);
  }

  #onCart = () => this.refresher.schedule();
}

if (!customElements.get('cw-basket-content')) customElements.define('cw-basket-content', CwBasketContent);
if (!customElements.get('cw-basket')) customElements.define('cw-basket', CwBasket);
if (!customElements.get('cw-cart-page')) customElements.define('cw-cart-page', CwCartPage);
